import styles from "./SizeGroups.module.scss";

import { useState } from "react";
import classNames from "classnames";
import { ShowMore } from "./components/ShowMore/ShowMore";
import { SizeHeader } from "./components/SizeHeader/SizeHeader";
import { booleanObj } from "./types";
import { getSizeGroupValues } from "./utils";
import { SizeError } from "./components/SizeError/SizeError";

type Sizes = {
  size: string;
  sizeHeadline: string;
  isAvailable: boolean;
  isSelected: boolean;
  isSizeReducedToClear: boolean;
}[];

type SizeButtonsWithShowMoreProps = {
  sizes: Sizes;
  isSizeSelected: boolean;
  currentSize: string;
  showMore?: number;
  isBabyAndToddlerCategory: boolean;
  onClick: (size: string) => void;
};

export function SizeGroupsUI({
  sizes,
  currentSize,
  isSizeSelected,
  onClick,
  showMore,
  isBabyAndToddlerCategory,
}: SizeButtonsWithShowMoreProps) {
  const {
    groupedSizes,
    showMoreMinThreshold,
    showMoremessage,
    showLessmessage,
    canShowMore,
  } = getSizeGroupValues({ sizes, showMore, isBabyAndToddlerCategory });

  let foundKey: string | undefined = undefined;

  for (const key in groupedSizes) {
    const values: Sizes = groupedSizes[key];
    if (
      values.findIndex((g) => g.isSelected === true) >=
      showMoreMinThreshold[key]
    ) {
      foundKey = key;
    }
  }

  const [isShowingMore, setIsShowingMore] = useState<booleanObj>(
    Object.keys(groupedSizes).reduce(
      (prev, curr) => ({
        ...prev,
        [curr]: curr === foundKey || foundKey === "",
      }),
      {},
    ),
  );

  if (sizes.length === 0) {
    return null;
  }

  const maxStringLength = Math.max(...sizes.map(({ size }) => size.length));
  const sizesContainerStyle = classNames({
    [styles.sizesContainer]: true,
    [styles.fourButtons]: maxStringLength > 3 && maxStringLength < 7,
    [styles.threeButtons]: maxStringLength >= 7 && maxStringLength < 10,
    [styles.twoButtons]: maxStringLength >= 10,
  });
  return (
    <>
      <SizeError
        sizes={sizes.map((size) => ({
          name: size.size,
        }))}
      ></SizeError>
      <SizeHeader sizeValue={currentSize}></SizeHeader>

      {Object.keys(groupedSizes).map((group) => {
        return (
          <div className={sizesContainerStyle} key={group}>
            {group && <h3 className={styles.heading}>{group}</h3>}
            <ul className={styles.list} data-cy="size-selector-list">
              {groupedSizes[group].map(
                (
                  { size, isAvailable, isSizeReducedToClear, isSelected },
                  idx,
                ) => {
                  const ariaLabel = getAriaLabel(
                    isSizeSelected,
                    currentSize,
                    size,
                    isAvailable,
                  );
                  if (
                    isShowingMore[group] ||
                    (!isShowingMore[group] && idx < showMoreMinThreshold[group])
                  )
                    return (
                      <li key={size} className={styles.item}>
                        <button
                          data-cy="size-selector-item"
                          onClick={() => {
                            onClick(size);
                          }}
                          data-size={size}
                          className={classNames(styles["size-button"], {
                            [styles["size-button--active"]]: isSelected,
                            [styles["size-button--out-of-stock"]]: !isAvailable,
                          })}
                          aria-label={ariaLabel}
                        >
                          {size}
                          {isSizeReducedToClear && (
                            <p className={styles["reduced"]}>Reduced</p>
                          )}
                        </button>
                      </li>
                    );
                  return null;
                },
              )}
            </ul>

            <ShowMore
              group={group}
              showLessmessage={showLessmessage}
              showMoreMessage={showMoremessage}
              setIsShowingMore={setIsShowingMore}
              isShowingMore={isShowingMore}
              canShowMore={canShowMore}
            ></ShowMore>
          </div>
        );
      })}
    </>
  );
}
function getAriaLabel(
  isSizeSelected: boolean,
  currentSize: string,
  size: string,
  isAvailable: boolean,
) {
  return isSizeSelected && currentSize === size
    ? `${size}. This size is selected${isAvailable ? "" : " but unavailable"}`
    : `${size}. This size is ${isAvailable ? "available" : "unavailable"}`;
}
